import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { IconButton, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import axios from 'axios';
import { PlanTakeoffProvider } from './PlanTakeoffContext';
import PlanMapContainer from './map/PlanMapContainer';
import PlanTakeoffSidebar from './sidebar/PlanTakeoffSidebar';
import CalibrationDialog from './dialogs/CalibrationDialog';
import SymbolPickerModal from './dialogs/SymbolPickerModal';
import SymbolWizard from './dialogs/SymbolWizard';
import usePlanDrawing from './hooks/usePlanDrawing';
import usePlanRendering from './hooks/usePlanRendering';
import usePlanItems from './hooks/usePlanItems';
import usePlacementEditing from './hooks/usePlacementEditing';
import useCalibration from './hooks/useCalibration';
import useCvMatching from './hooks/useCvMatching';
import { v4 as uuidv4 } from 'uuid';
import useCandidateRendering from './hooks/useCandidateRendering';
import CandidateReviewPanel from './sidebar/CandidateReviewPanel';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    background: '#1e1e1e',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
    background: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 500,
    flex: 1,
  },
  headerMeta: {
    fontSize: 12,
    color: '#888',
  },
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  mapArea: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
    overflow: 'hidden',
  },
}));

const PlanTakeoffWorkspace = (props) => {
  const classes = useStyles();
  const { projectId, docId } = useParams();
  const history = useHistory();
  const url = props.url;

  // Map refs
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingAssembly, setImportingAssembly] = useState(false);
  const [document, setDocument] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(0);
  const [takeoffId, setTakeoffId] = useState(null);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [activeBuilding, setActiveBuilding] = useState(null);
  const [completedAreas, setCompletedAreas] = useState([]);

  // Load document data + page images + project buildings
  useEffect(() => {
    if (!url || !projectId || !docId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch full document data, page images, and project (for buildings)
        const [viewResp, imgResp, projResp] = await Promise.all([
          axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/view`),
          axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/page-images`),
          axios.get(`${url}/api/bid-estimation/projects/${projectId}`),
        ]);

        // Buildings from project
        const project = projResp.data?.project || projResp.data || {};
        if (!cancelled && Array.isArray(project.buildings)) {
          setBuildings(project.buildings);
        }

        // /view returns { document, parseResult, parseStatus, pageSummary, ... }
        const rawDoc = viewResp.data?.document || viewResp.data || {};
        const fullParseResult = viewResp.data?.parseResult || rawDoc.parseResult || {};
        const doc = { ...rawDoc, parseResult: fullParseResult };
        if (!cancelled) setDocument(doc);

        if (!cancelled && imgResp.data?.pages) {
          const sheetIndex = fullParseResult?.sheetIndex || [];
          const pageData = imgResp.data.pages.map((p, i) => {
            const sheetInfo = sheetIndex.find(s => s.page === (p.page ?? i));
            return {
              page: p.page ?? i,
              url: p.url,
              width: p.width || 2400,
              height: p.height || 1800,
              title: sheetInfo?.title || `Page ${(p.page ?? i) + 1}`,
              discipline: sheetInfo?.discipline || '',
            };
          });
          setPages(pageData);
        }

        // Check for existing plan takeoff
        const takeoffResp = await axios.get(`${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs`, {
          params: { documentId: docId },
        });
        const takeoffList = Array.isArray(takeoffResp.data) ? takeoffResp.data : (takeoffResp.data?.takeoffs || []);
        const existing = takeoffList[0];
        if (!cancelled && existing) {
          setTakeoffId(existing._id);
          setActivePage(existing.activePage || 0);
        }
      } catch (err) {
        console.error('Failed to load plan takeoff data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url, projectId, docId]);

  // Current page image info
  const currentPage = pages[activePage] || null;
  const imageUrl = currentPage?.url || '';
  const imageWidth = currentPage?.width || 2400;
  const imageHeight = currentPage?.height || 1800;

  // Per-page calibrations: { [pageIndex]: { pixelDistance, realDistance, unit } }
  const [calibrations, setCalibrations] = useState({});

  // Calibration hook manages the drawing UI for the current page
  const {
    calibration, setCalibration, calibrating, pendingLine,
    startCalibration, confirmCalibration: rawConfirmCalibration, clearCalibration, pixelsPerFoot,
  } = useCalibration({ mapRef, drawnItemsRef });

  // When page changes, load that page's saved calibration
  useEffect(() => {
    const pageCal = calibrations[activePage];
    if (pageCal?.pixelDistance) {
      setCalibration(pageCal);
    } else {
      clearCalibration();
    }
  }, [activePage]); // eslint-disable-line

  // Wrap confirmCalibration to also save per-page
  const confirmCalibration = useCallback((realDistance, unit) => {
    rawConfirmCalibration(realDistance, unit);
    // The useCalibration hook will update `calibration` state — we need to save it per page
    // We compute pixelsPerFoot here since the hook does it internally
    const pendingPixelDist = pendingLine ? Math.sqrt(
      Math.pow(pendingLine[1].x - pendingLine[0].x, 2) +
      Math.pow(pendingLine[1].y - pendingLine[0].y, 2)
    ) : 0;
    let realFeet = realDistance;
    if (unit === 'in') realFeet = realDistance / 12;
    else if (unit === 'meters') realFeet = realDistance * 3.28084;
    const cal = { pixelDistance: pendingPixelDist, realDistance, unit, realFeet };
    setCalibrations(prev => ({ ...prev, [activePage]: cal }));
  }, [rawConfirmCalibration, pendingLine, activePage]);

  // Items from parsed data
  const parseResult = document?.parseResult || null;
  const {
    items, setItems, skipItem, resetItem,
    updateItemGeometryType, setItemSymbol, setItemBuilding,
    addPlacement, updatePlacement, removePlacement,
    groupedItems, summary, activeItemId, setActiveItemId,
  } = usePlanItems(parseResult, null);

  // Expand/collapse state for sidebar cards
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activePlacementId, setActivePlacementId] = useState(null);

  const toggleExpand = useCallback((itemId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  // CV Matching
  const {
    matching, matchItemId, candidates,
    runMatch, acceptCandidate, rejectCandidate,
    acceptAllAbove, rejectAllBelow, clearCandidates,
  } = useCvMatching({ url, projectId, takeoffId, addPlacement, activePage });

  useCandidateRendering({
    mapRef, candidates, matchItemId,
    onCandidateClick: acceptCandidate,
  });

  const handleFindOnPage = useCallback((itemId) => {
    runMatch(itemId, activePage);
  }, [runMatch, activePage]);

  const handleCvDone = useCallback(() => {
    clearCandidates();
  }, [clearCandidates]);

  const [symbolPickerItemId, setSymbolPickerItemId] = useState(null);
  const [symbolWizardOpen, setSymbolWizardOpen] = useState(false);

  // Drawing — unified for all geometry types
  const handleItemDrawn = useCallback((result) => {
    if (!activeItemId) return;
    const item = items.find(i => i.itemId === activeItemId);
    if (!item) return;

    if (activeBuilding) setItemBuilding(activeItemId, activeBuilding);

    const placement = {
      id: uuidv4(),
      page: activePage,
      pixelPath: result.pixelPaths,
      measuredQty: result.measuredQty || 0,
      measuredUnit: result.measuredUnit || '',
    };
    addPlacement(activeItemId, placement);
    setExpandedItems(prev => new Set(prev).add(activeItemId));
  }, [activeItemId, items, activePage, addPlacement, activeBuilding, setItemBuilding]);

  const { startDraw } = usePlanDrawing({
    mapRef, drawnItemsRef,
    pixelsPerFoot,
    onItemDrawn: handleItemDrawn,
  });

  // Editing state lifted up so both rendering and editing hooks share it
  const [editingPlacement, setEditingPlacement] = useState(null);

  // Rendering placed items — per-page, per-placement
  const { layerMapRef } = usePlanRendering({
    mapRef, drawnItemsRef, items,
    activeItemId, activePage,
    onPlacementClick: (itemId, placementId) => {
      if (editingPlacement) return;
      setActiveItemId(itemId);
      setActivePlacementId(placementId);
      setExpandedItems(prev => new Set(prev).add(itemId));
    },
    pixelsPerFoot,
    showMeasurements,
    editingPlacement,
  });

  // Placement editing (vertex editing, marker dragging)
  const {
    startEdit, confirmEdit, cancelEdit,
  } = usePlacementEditing({
    mapRef, layerMapRef, items, updatePlacement,
    pixelsPerFoot, activePage, setActivePage,
    editingPlacement, setEditingPlacement,
  });

  // Cancel editing when page changes
  useEffect(() => {
    if (editingPlacement) cancelEdit();
  }, [activePage]); // eslint-disable-line

  // Add placement from sidebar (enters draw mode)
  const handleAddPlacement = useCallback((itemId) => {
    if (editingPlacement) cancelEdit();
    setActiveItemId(itemId);
    const item = items.find(i => i.itemId === itemId);
    if (!item) return;
    const tool = item.geometryType === 'point' ? 'marker'
      : item.geometryType === 'polyline' ? 'polyline'
      : 'polygon';
    startDraw(tool);
  }, [items, startDraw, setActiveItemId, editingPlacement, cancelEdit]);

  // Sidebar placement row click → navigate to page + select
  const handleSelectItem = useCallback((itemId, placementId) => {
    setActiveItemId(itemId);
    if (placementId) {
      setActivePlacementId(placementId);
      const item = items.find(i => i.itemId === itemId);
      const placement = item?.placements?.find(p => p.id === placementId);
      if (placement && placement.page !== activePage) {
        setActivePage(placement.page);
      }
    }
  }, [items, activePage, setActiveItemId, setActivePage]);

  // Save
  const handleSave = useCallback(async () => {
    if (!url || !projectId) return;
    setSaving(true);
    try {
      const payload = {
        items,
        calibration,
        calibrations,
        activePage,
        completedAreas,
        name: document?.name || 'Plan Takeoff',
      };

      if (takeoffId) {
        await axios.put(`${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs/${takeoffId}`, payload);
        return takeoffId;
      } else {
        const resp = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs`, {
          documentId: docId,
          ...payload,
        });
        const created = resp.data?.takeoff || resp.data;
        if (created?._id) {
          setTakeoffId(created._id);
          return created._id;
        }
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [url, projectId, docId, takeoffId, items, calibration, calibrations, activePage, completedAreas, document]);

  const handleImportToAssembly = useCallback(async () => {
    if (!url || !projectId) return;
    setImportingAssembly(true);
    try {
      const savedTakeoffId = await handleSave();
      if (!savedTakeoffId) return;
      await axios.post(`${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs/${savedTakeoffId}/import-line-items`);
      await axios.post(`${url}/api/bid-estimation/projects/${projectId}/scope/generate`);
      history.push(`/bid-estimation/${projectId}/scope`);
    } catch (err) {
      console.error('Import to assembly failed:', err);
    } finally {
      setImportingAssembly(false);
    }
  }, [url, projectId, handleSave, history]);

  // Load saved state when takeoff loads
  useEffect(() => {
    if (!takeoffId || !url || !projectId) return;
    (async () => {
      try {
        const resp = await axios.get(`${url}/api/bid-estimation/projects/${projectId}/plan-takeoffs/${takeoffId}`);
        const t = resp.data?.takeoff || resp.data;
        if (t) {
          // Load per-page calibrations (new), with fallback to legacy single calibration
          if (t.calibrations && typeof t.calibrations === 'object') {
            const calMap = t.calibrations instanceof Map
              ? Object.fromEntries(t.calibrations)
              : t.calibrations;
            setCalibrations(calMap);
          } else if (t.calibration?.pixelDistance) {
            // Legacy: single calibration, assign to page 0
            setCalibrations({ 0: t.calibration });
          }
          if (t.items?.length) {
            setItems(t.items.map(item => {
              const sr = item.sourceRef || {};
              if (sr.sheet == null && sr.sheetIdx != null) {
                sr.sheet = sr.sheetIdx;
                sr.table = sr.tableIdx;
                sr.row = sr.rowIdx;
              }
              return { ...item, sourceRef: sr };
            }));
          }
          if (typeof t.activePage === 'number') setActivePage(t.activePage);
          if (Array.isArray(t.completedAreas)) setCompletedAreas(t.completedAreas);
        }
      } catch (_) {}
    })();
  }, [takeoffId, url, projectId]);

  const contextValue = useMemo(() => ({
    url, projectId, docId, document, pages, activePage, setActivePage,
    items, groupedItems, summary, activeItemId, setActiveItemId,
    skipItem, resetItem, updateItemGeometryType,
    addPlacement, updatePlacement, removePlacement,
    calibration, calibrating, pixelsPerFoot, startCalibration,
    startDraw, handleAddPlacement,
    saving, handleSave,
    mapRef, drawnItemsRef,
  }), [
    url, projectId, docId, document, pages, activePage,
    items, groupedItems, summary, activeItemId,
    skipItem, resetItem, updateItemGeometryType,
    addPlacement, updatePlacement, removePlacement,
    calibration, calibrating, pixelsPerFoot, startCalibration,
    startDraw, handleAddPlacement,
    saving, handleSave,
  ]);

  return (
    <PlanTakeoffProvider value={contextValue}>
      <div className={classes.root}>
        <div className={classes.header}>
          <IconButton size="small" onClick={() => history.goBack()}>
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.headerTitle}>
            Plan Takeoff — {document?.name || 'Loading...'}
          </Typography>
          <span className={classes.headerMeta}>
            {pages.length} pages · {summary.totalPlaced}/{items.length} items placed
            {pixelsPerFoot > 0 && ` · Scale: 1px = ${(1/pixelsPerFoot).toFixed(3)}ft`}
          </span>
        </div>

        <div className={classes.body}>
          <div className={classes.mapArea}>
            <PlanMapContainer
              mapRef={mapRef}
              drawnItemsRef={drawnItemsRef}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              loading={loading}
              calibrated={pixelsPerFoot > 0}
              calibrating={calibrating}
              showMeasurements={showMeasurements}
              onToggleMeasurements={() => setShowMeasurements(v => !v)}
              onDrawPolygon={() => startDraw('polygon')}
              onDrawPolyline={() => startDraw('polyline')}
              onDrawPoint={() => startDraw('marker')}
              onCalibrate={startCalibration}
              matching={matching}
              activeSymbolUrl={activeItemId ? (items.find(i => i.itemId === activeItemId)?.symbolDataUrl || null) : null}
              activeItemName={activeItemId ? (items.find(i => i.itemId === activeItemId)?.description || '') : ''}
            />
          </div>

          {matchItemId && candidates.length > 0 ? (
            <CandidateReviewPanel
              candidates={candidates}
              itemName={items.find(i => i.itemId === matchItemId)?.description || ''}
              onAcceptCandidate={acceptCandidate}
              onRejectCandidate={rejectCandidate}
              onAcceptAllAbove={acceptAllAbove}
              onRejectAllBelow={rejectAllBelow}
              onDone={handleCvDone}
            />
          ) : (
            <PlanTakeoffSidebar
              pages={pages}
              activePage={activePage}
              onSelectPage={setActivePage}
              groupedItems={groupedItems}
              activeItemId={activeItemId}
              activePlacementId={activePlacementId}
              onSelectItem={handleSelectItem}
              onSkipItem={skipItem}
              onChangeType={updateItemGeometryType}
              summary={summary}
              saving={saving}
              onSave={handleSave}
              importingAssembly={importingAssembly}
              onImportToAssembly={handleImportToAssembly}
              cropBaseUrl={`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}`}
              onPickSymbol={setSymbolPickerItemId}
              onFindOnPage={handleFindOnPage}
              onOpenSymbolWizard={() => setSymbolWizardOpen(true)}
              expandedItems={expandedItems}
              onToggleExpand={toggleExpand}
              onEditPlacement={startEdit}
              onDeletePlacement={removePlacement}
              onAddPlacement={handleAddPlacement}
              onConfirmEdit={confirmEdit}
              onCancelEdit={cancelEdit}
              editingPlacement={editingPlacement}
              buildings={buildings}
              activeBuilding={activeBuilding}
              onSetActiveBuilding={setActiveBuilding}
              completedAreas={completedAreas}
              onToggleAreaComplete={(areaName) => {
                setCompletedAreas(prev =>
                  prev.includes(areaName)
                    ? prev.filter(a => a !== areaName)
                    : [...prev, areaName]
                );
              }}
              onAddBuilding={async (name) => {
                const trimmed = (name || '').trim();
                if (!trimmed || buildings.find(b => b.name === trimmed)) return;
                const updated = [...buildings, { name: trimmed }];
                setBuildings(updated);
                setActiveBuilding(trimmed);
                try {
                  await axios.put(`${url}/api/bid-estimation/projects/${projectId}`, { buildings: updated });
                } catch (_) {}
              }}
            />
          )}
        </div>

        <CalibrationDialog
          open={!!pendingLine}
          onConfirm={confirmCalibration}
          onCancel={() => clearCalibration()}
        />

        <SymbolPickerModal
          open={!!symbolPickerItemId}
          onClose={() => setSymbolPickerItemId(null)}
          onSave={(dataUrl) => {
            if (symbolPickerItemId) setItemSymbol(symbolPickerItemId, dataUrl);
            setSymbolPickerItemId(null);
          }}
          imageUrl={(() => {
            // Use the table's legend crop from the crop-preview endpoint (no rowIndex = full legend)
            if (!symbolPickerItemId) return imageUrl;
            const item = items.find(i => i.itemId === symbolPickerItemId);
            const sr = item?.sourceRef;
            if (!sr || sr.sheet == null || sr.table == null) return imageUrl;
            return `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/sheets/${sr.sheet}/tables/${sr.table}/crop-preview?artifactIndex=0&pad=20`;
          })()}
          itemName={symbolPickerItemId ? (items.find(i => i.itemId === symbolPickerItemId)?.description || '') : ''}
        />

        <SymbolWizard
          open={symbolWizardOpen}
          onClose={() => setSymbolWizardOpen(false)}
          items={items}
          getLegendUrl={(item) => {
            const sr = item?.sourceRef;
            if (!sr || sr.sheet == null || sr.table == null) return imageUrl;
            return `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/sheets/${sr.sheet}/tables/${sr.table}/crop-preview?artifactIndex=0&pad=20`;
          }}
          onSaveSymbol={setItemSymbol}
        />
      </div>
    </PlanTakeoffProvider>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
});

export default connect(mapStateToProps)(PlanTakeoffWorkspace);
